import { alpha, createTheme } from "@mui/material/styles";
import { BrandColors, NeutralColors, Typography as BrandTypography } from "../branding";
// Clean white with subtle warm tint
const BACKGROUND_DEFAULT = "#F8FAFC"; // Tailwind slate-50, cooler blue-grey matching design system
const BACKGROUND_PAPER = "#FFFFFF"; // pure white for cards/surfaces
const SHADOW_TINT = "#1B3A6B"; // deep navy used as shadow/overlay tint throughout
const NAV_BORDER = alpha(SHADOW_TINT, 0.08);
const DIVIDER = "rgba(15, 23, 42, 0.08)"; // slightly cooler base (#0F172A) for better contrast
let theme = createTheme({
    palette: {
        chart: [
            BrandColors.primary.main,
            BrandColors.success.main,
            BrandColors.warning.main,
            BrandColors.secondary.main,
            BrandColors.error.main,
            BrandColors.info.main,
        ],
        primary: {
            main: BrandColors.primary.main,
            light: BrandColors.primary.light,
            dark: BrandColors.primary.dark,
            contrastText: BrandColors.primary.contrast,
        },
        secondary: {
            main: BrandColors.secondary.main,
            light: BrandColors.secondary.light,
            dark: BrandColors.secondary.dark,
            contrastText: BrandColors.secondary.contrast,
        },
        success: {
            main: BrandColors.success.main,
            light: BrandColors.success.light,
            dark: BrandColors.success.dark,
        },
        error: {
            main: BrandColors.error.main,
            light: BrandColors.error.light,
            dark: BrandColors.error.dark,
        },
        warning: {
            main: BrandColors.warning.main,
            light: BrandColors.warning.light,
            dark: BrandColors.warning.dark,
        },
        info: {
            main: BrandColors.info.main,
            light: BrandColors.info.light,
            dark: BrandColors.info.dark,
        },
        text: {
            primary: NeutralColors[900], // back to darker text for better readability
            secondary: NeutralColors[600],
            disabled: NeutralColors[400],
        },
        background: {
            default: BACKGROUND_DEFAULT,
            paper: BACKGROUND_PAPER,
        },
        divider: DIVIDER,
        grey: {
            50: NeutralColors[50],
            100: NeutralColors[100],
            200: NeutralColors[200],
            300: NeutralColors[300],
            400: NeutralColors[400],
            500: NeutralColors[500],
            600: NeutralColors[600],
            700: NeutralColors[700],
            800: NeutralColors[800],
            900: NeutralColors[900],
        },
    },
    shape: {
        // DS-ADR 0009: dashboard base stays 10 until frontend/src has zero unitless borderRadius literals (scripts/lib/style-literal-baseline.txt), then 12.
        borderRadius: 10,
    },
    typography: {
        fontFamily: BrandTypography.fontFamily.sans,
        fontWeightLight: BrandTypography.fontWeight.light,
        fontWeightRegular: BrandTypography.fontWeight.regular,
        fontWeightMedium: BrandTypography.fontWeight.medium,
        fontWeightBold: BrandTypography.fontWeight.semibold,
        // Keep Inter for all headings for consistency
        h1: {
            fontSize: "2.5rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.2,
        },
        h2: {
            fontSize: "2rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.2,
        },
        h3: {
            fontSize: "1.5rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.3,
        },
        h4: {
            fontSize: "1.25rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.4,
            color: "#0F172A",
        },
        h5: {
            fontSize: "1.125rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.4,
        },
        h6: {
            fontSize: "1rem",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.5,
        },
        body1: {
            fontSize: "1rem",
            lineHeight: 1.6,
            color: "#475569",
        },
        body2: {
            fontSize: "0.875rem",
            lineHeight: 1.5,
            color: "#64748B",
        },
        button: {
            fontWeight: 500,
            letterSpacing: "-0.005em",
            textTransform: "none",
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: BACKGROUND_DEFAULT,
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                },
                "@media print": {
                    "body, :root, html": {
                        backgroundColor: "#ffffff !important",
                        background: "#ffffff !important",
                        WebkitPrintColorAdjust: "exact !important",
                        printColorAdjust: "exact !important",
                    },
                    ".woot-widget-bubble, .woot-widget-holder, .woot--bubble-holder, .woot-widget-wrap, [class*='woot'], #chatwoot-widget": {
                        display: "none !important",
                    },
                    "main, [role='main'], main.MuiBox-root": {
                        width: "100% !important",
                        maxWidth: "100% !important",
                        marginLeft: "0 !important",
                        margin: "0 !important",
                        padding: "0 !important",
                        flexGrow: "1 !important",
                    },
                    "#root > div, #root > .MuiBox-root": {
                        display: "block !important",
                    },
                    ".MuiBox-root + .MuiBox-root": {
                        marginLeft: "0 !important",
                        width: "100% !important",
                    },
                },
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: "rgba(255, 255, 255, 0.92)",
                    backdropFilter: "blur(20px) saturate(1.2)",
                    borderBottom: `1px solid ${NAV_BORDER}`,
                    color: NeutralColors[800],
                    borderRadius: 0,
                    "@media print": {
                        display: "none !important",
                    },
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    boxShadow: "none",
                    "@media print": {
                        display: "none !important",
                        minHeight: "0 !important",
                    },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    "@media print": {
                        display: "none !important",
                    },
                },
                paper: {
                    // Drawer: white-ish surface so it reads as a distinct panel from parchment page
                    backgroundColor: "#FFFFFF",
                    borderRadius: 0,
                    borderRight: `1px solid ${NAV_BORDER}`,
                    boxShadow: "2px 0 8px 0 rgba(27, 58, 107, 0.06)",
                    color: NeutralColors[800],
                    "@media print": {
                        display: "none !important",
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: "1px 8px",
                    padding: "9px 14px",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    color: NeutralColors[500],
                    "&:hover": {
                        backgroundColor: "rgba(27, 58, 107, 0.06)",
                        color: NeutralColors[700],
                    },
                    "&.Mui-selected": {
                        backgroundColor: "rgba(27, 58, 107, 0.08)",
                        color: BrandColors.primary.main,
                        borderLeft: `3px solid ${BrandColors.primary.main}`,
                        paddingLeft: "11px",
                        "&:hover": {
                            backgroundColor: "rgba(27, 58, 107, 0.12)",
                        },
                        "& .MuiListItemIcon-root": {
                            color: BrandColors.primary.main,
                        },
                        "& .MuiListItemText-primary": {
                            color: BrandColors.primary.main,
                            fontWeight: 600,
                        },
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 38,
                    color: NeutralColors[400],
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 9,
                    padding: "9px 18px",
                    fontSize: "0.875rem",
                    letterSpacing: "-0.005em",
                    transition: "background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease",
                },
                contained: {
                    boxShadow: "0 1px 2px 0 rgba(27, 58, 107, 0.25), 0 1px 3px 0 rgba(27, 58, 107, 0.12)",
                    "&:hover": {
                        boxShadow: "0 4px 14px 0 rgba(27, 58, 107, 0.3), 0 2px 4px 0 rgba(27, 58, 107, 0.15)",
                        transform: "scale(1.01)",
                    },
                    "&:active": {
                        transform: "scale(0.99)",
                        boxShadow: "0 1px 2px 0 rgba(27, 58, 107, 0.2)",
                    },
                },
                outlined: {
                    borderWidth: 1.5,
                    "&:hover": {
                        borderWidth: 1.5,
                        backgroundColor: "rgba(27, 58, 107, 0.05)",
                    },
                },
                text: {
                    "&:hover": {
                        backgroundColor: "rgba(27, 58, 107, 0.06)",
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    backgroundColor: BACKGROUND_PAPER,
                    boxShadow: "0 1px 2px 0 rgba(27, 58, 107, 0.06), 0 1px 4px 0 rgba(27, 58, 107, 0.04)",
                    borderRadius: 12,
                    border: `1px solid rgba(27, 58, 107, 0.08)`,
                },
                elevation1: {
                    boxShadow: "0 2px 8px -1px rgba(27, 58, 107, 0.09), 0 1px 3px 0 rgba(27, 58, 107, 0.05)",
                },
                elevation2: {
                    boxShadow: "0 8px 24px -4px rgba(27, 58, 107, 0.12), 0 2px 6px -1px rgba(27, 58, 107, 0.07)",
                },
                elevation3: {
                    boxShadow: "0 16px 40px -8px rgba(27, 58, 107, 0.14), 0 4px 12px -2px rgba(27, 58, 107, 0.08)",
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    backgroundColor: BACKGROUND_PAPER,
                    boxShadow: "0 1px 2px 0 rgba(27, 58, 107, 0.06), 0 1px 4px 0 rgba(27, 58, 107, 0.04)",
                    borderRadius: 12,
                    border: `1px solid rgba(27, 58, 107, 0.08)`,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 9,
                        backgroundColor: BACKGROUND_PAPER,
                        transition: "box-shadow 0.15s ease",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(27, 58, 107, 0.4)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 2,
                            borderColor: BrandColors.primary.main,
                        },
                        "&.Mui-focused": {
                            boxShadow: "0 0 0 3px rgba(27, 58, 107, 0.12)",
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 9,
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 2,
                        borderColor: BrandColors.primary.main,
                        boxShadow: "0 0 0 3px rgba(27, 58, 107, 0.12)",
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 9,
                    border: "1px solid transparent",
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    backgroundColor: NeutralColors[100],
                    borderRadius: 999,
                    padding: 3,
                    gap: 1,
                    border: "none",
                    "& .MuiToggleButtonGroup-grouped": {
                        border: "none",
                        "&:not(:first-of-type)": {
                            borderRadius: 999,
                            marginLeft: 0,
                        },
                        "&:first-of-type": {
                            borderRadius: 999,
                        },
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    border: "none",
                    padding: "5px 11px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    lineHeight: 1,
                    textTransform: "none",
                    color: NeutralColors[500],
                    backgroundColor: "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": {
                        backgroundColor: alpha(SHADOW_TINT, 0.06),
                    },
                    "&.Mui-selected": {
                        color: BrandColors.primary.main,
                        backgroundColor: BACKGROUND_PAPER,
                        boxShadow: `0 1px 2px 0 ${alpha(SHADOW_TINT, 0.08)}`,
                        "&:hover": {
                            backgroundColor: BACKGROUND_PAPER,
                        },
                    },
                },
                sizeSmall: {
                    padding: "5px 11px",
                    fontSize: "0.75rem",
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: DIVIDER,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    letterSpacing: "0em",
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: NeutralColors[800],
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    borderRadius: 6,
                    padding: "5px 10px",
                },
                arrow: {
                    color: NeutralColors[800],
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    letterSpacing: "-0.005em",
                    minHeight: 44,
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    // Let the parent Paper control border-radius; this prevents clipping issues
                    borderRadius: "inherit",
                    overflow: "hidden",
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    "& .MuiTableCell-head": {
                        fontWeight: 700,
                        fontSize: "0.6875rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        // 400 on NeutralColors[50] is 2.45:1 — below the 4.5:1 floor at
                        // 11px (no large-text allowance). Pinned by
                        // tableHeadContrast.test.ts (H2, WCAG audit 2026-08-18).
                        color: NeutralColors[600],
                        backgroundColor: NeutralColors[50],
                        borderBottom: `1px solid ${NeutralColors[200]}`,
                        paddingTop: 12,
                        paddingBottom: 12,
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&:last-child .MuiTableCell-body": {
                        borderBottom: "none",
                    },
                    "&:hover .MuiTableCell-body": {
                        backgroundColor: NeutralColors[50],
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottomColor: NeutralColors[100],
                    fontSize: "0.875rem",
                    padding: "14px 16px",
                    "&:first-of-type": { paddingLeft: 24 },
                    "&:last-of-type": { paddingRight: 24 },
                },
            },
        },
    },
});
theme = createTheme(theme, {
    palette: {
        surface: theme.palette.augmentColor({
            color: { main: BACKGROUND_PAPER },
            name: "surface",
        }),
    },
});
export default theme;
